/**
 * Memory API Client with Safety Nets
 * Provides localStorage fallback and gradual migration support
 */

import { authedFetch } from "./authedFetch";

// Support both AWS (production) and local development
const USE_LOCAL_DEV = process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_LOCAL_LAMBDAS === 'true';

// Professional local storage options
// eslint-disable-next-line no-unused-vars
const USE_SQLITE = USE_LOCAL_DEV && true; // Set to false to use localStorage instead
const BYPASS_MEMORY_API = false; // Use V2 memory endpoints even in local dev
const API_BASE_URL = USE_LOCAL_DEV 
  ? 'http://localhost:3001' 
  : (process.env.REACT_APP_API_GATEWAY_URL || 'https://api.limkc.com');
const MEMORY_ENDPOINT = USE_LOCAL_DEV ? '/memory-v2' : '/upload/memory';

// Feature flags for gradual rollout
const FEATURE_FLAGS = {
  USE_MEMORY_API: true,  // Force enable Memory API - no localStorage allowed
  DUAL_WRITE: false,  // Pure DynamoDB mode - no localStorage
  LOG_ERRORS: true,  // Log errors for monitoring
  FALLBACK_TO_LOCAL: false  // Pure cloud storage - no fallback
};

/**
 * Handle auth errors - trigger logout on 401/403
 * Only shows "session expired" if user was actually logged in
 */
const handleAuthError = (response) => {
  if (response.status === 401 || response.status === 403) {
    // accessToken now lives in HttpOnly cookie — JS cannot clear it directly;
    // logout API call clears it server-side via Set-Cookie Max-Age=0.
    // idToken is still in localStorage (out of cookie-migration scope) and
    // serves as the "was logged in" sentinel for the forceLogout event.
    const wasLoggedIn = !!localStorage.getItem('idToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('userLoggedIn');
    if (wasLoggedIn) {
      window.dispatchEvent(new Event('forceLogout'));
    }
    throw new Error('Authentication required');
  }
};

class MemoryApi {
  constructor() {
    this.apiAvailable = false;
    this.testApiConnection();
  }

  /**
   * Test if Memory API is available
   */
  async testApiConnection() {
    if (!FEATURE_FLAGS.USE_MEMORY_API) {
      console.log('Memory API disabled by feature flag');
      return;
    }

    // Force enable API - skip health check since Memory Lambda is deployed
    this.apiAvailable = true;
    // console.log('Memory API force enabled - pure cloud storage mode');
  }

  // ============================================================================
  // SESSION MANAGEMENT METHODS
  // ============================================================================

  /**
   * List all chat sessions for current user (full list — backwards-compat
   * call still used by mobile and any non-paginated caller).
   */
  async listSessions() {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'listSessions' })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();
      return result.sessions || [];
    } catch (error) {
      console.error('Failed to list sessions:', error);
      throw error;
    }
  }

  /**
   * Paginated chat-session list. Returns one page (default 50) plus an
   * opaque cursor for the next page (or null at end of list). Used by the
   * sidebar's usePaginatedCache hook.
   *
   * Backend (chatty-memory-manager handlers/sessions.py) interprets:
   *   limit + cursor present → GSI-paged response
   *   neither sent             → full-list (preserved for mobile)
   *
   * Returned shape is normalized for usePaginatedCache:
   *   { items: [...sessions], next_cursor: "<opaque>" | null }
   */
  async listSessionsPage({ limit = 50, cursor = null } = {}) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    const body = { action: 'listSessions', limit };
    if (cursor) body.cursor = cursor;

    const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      handleAuthError(response);
      throw new Error(`API error: ${response.status}`);
    }

    const result = await response.json();
    return {
      items: result.sessions || [],
      next_cursor: result.next_cursor || null,
    };
  }

  /**
   * Create a new chat session
   */
  async createSession(title = 'New Chat') {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'createSession', title })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to create session:', error);
      throw error;
    }
  }

  /**
   * Delete a chat session
   */
  async deleteSession(sessionId) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'deleteSession', session_id: sessionId })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to delete session:', error);
      throw error;
    }
  }

  /**
   * Rename a chat session
   */
  async renameSession(sessionId, title) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'renameSession', session_id: sessionId, title })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to rename session:', error);
      throw error;
    }
  }

  /**
   * Get session details
   */
  async getSession(sessionId) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'getSession', session_id: sessionId })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();
      return result.session || null;
    } catch (error) {
      console.error('Failed to get session:', error);
      throw error;
    }
  }

  /**
   * Update session's project assignment
   */
  async updateSessionProject(sessionId, projectId) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'updateSessionProject',
          session_id: sessionId,
          project_id: projectId // null to remove from project
        })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to update session project:', error);
      throw error;
    }
  }

  /**
   * Migrate legacy full_history to session format
   */
  async migrateToSessions() {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'migrateToSessions' })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to migrate to sessions:', error);
      throw error;
    }
  }

  // ============================================================================
  // PROJECT MANAGEMENT METHODS
  // ============================================================================

  /**
   * Paginated project list. Returns one page (default 50) plus an opaque
   * cursor for the next page (or null at end of list). Used by the
   * sidebar's usePaginatedCache hook.
   *
   * Same backend dispatch rules as listSessionsPage.
   */
  async listProjectsPage({ limit = 50, cursor = null } = {}) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    const body = { action: 'listProjects', limit };
    if (cursor) body.cursor = cursor;

    const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      handleAuthError(response);
      throw new Error(`API error: ${response.status}`);
    }

    const result = await response.json();
    return {
      items: result.projects || [],
      next_cursor: result.next_cursor || null,
    };
  }

  /**
   * List all projects for current user (full list — backwards-compat for
   * non-paginated callers).
   */
  async listProjects() {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'listProjects' })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();
      return result.projects || [];
    } catch (error) {
      console.error('Failed to list projects:', error);
      throw error;
    }
  }

  /**
   * Create a new project
   * @param {string} title - Project title (required)
   * @param {string} description - Project description (optional)
   */
  async createProject(title, description = '') {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'createProject', title, description })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to create project:', error);
      throw error;
    }
  }

  /**
   * Delete a project (unassigns sessions, doesn't delete them)
   * @param {string} projectId - Project ID to delete
   */
  async deleteProject(projectId) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'deleteProject', project_id: projectId })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to delete project:', error);
      throw error;
    }
  }

  /**
   * Delete a project AND all its sessions
   * @param {string} projectId - Project ID to delete
   */
  async deleteProjectWithSessions(projectId) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      // First, get all sessions in this project
      const sessions = await this.getProjectSessions(projectId);

      // Delete each session
      for (const session of sessions) {
        await this.deleteSession(session.session_id);
      }

      // Then delete the project itself
      const result = await this.deleteProject(projectId);

      return {
        ...result,
        deletedSessions: sessions.length
      };
    } catch (error) {
      console.error('Failed to delete project with sessions:', error);
      throw error;
    }
  }

  /**
   * Rename/update a project
   * @param {string} projectId - Project ID
   * @param {string} title - New title
   * @param {string} description - New description (optional)
   */
  async renameProject(projectId, title, description = null) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const payload = { action: 'renameProject', project_id: projectId, title };
      if (description !== null) {
        payload.description = description;
      }

      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to rename project:', error);
      throw error;
    }
  }

  /**
   * Update project instruction and/or memory
   * @param {string} projectId - Project ID
   * @param {object} updates - Fields to update { instruction, memory }
   */
  async updateProject(projectId, updates = {}) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const payload = { action: 'updateProject', project_id: projectId };

      if (updates.instruction !== undefined) {
        payload.instruction = updates.instruction;
      }
      if (updates.memory !== undefined) {
        payload.memory = updates.memory;
      }

      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to update project:', error);
      throw error;
    }
  }

  /**
   * Get all sessions belonging to a project
   * @param {string} projectId - Project ID
   */
  async getProjectSessions(projectId) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'getProjectSessions', project_id: projectId })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();
      return result.sessions || [];
    } catch (error) {
      console.error('Failed to get project sessions:', error);
      throw error;
    }
  }

  /**
   * Create a session within a project
   * @param {string} title - Session title
   * @param {string} projectId - Project ID to assign session to
   */
  async createSessionInProject(title = 'New Chat', projectId) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'createSession', title, project_id: projectId })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to create session in project:', error);
      throw error;
    }
  }

  // ============================================================================
  // PROJECT FILE METHODS
  // ============================================================================

  /**
   * Get presigned URL for uploading a file to S3
   * @param {string} projectId - Project ID
   * @param {string} filename - Original filename
   * @param {string} contentType - MIME type
   * @param {number} fileSize - File size in bytes
   */
  async getUploadUrl(projectId, filename, contentType, fileSize) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'getUploadUrl',
          project_id: projectId,
          filename,
          content_type: contentType,
          file_size: fileSize
        })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get upload URL:', error);
      throw error;
    }
  }

  /**
   * Confirm file upload and save metadata to DynamoDB
   * @param {string} projectId - Project ID
   * @param {string} fileId - File ID from getUploadUrl
   * @param {string} filename - Original filename
   * @param {number} sizeBytes - File size in bytes
   * @param {string} contentType - MIME type
   * @param {string} fileType - Type category ('image', 'pdf', 'docx', 'text', 'file')
   */
  async confirmUpload(projectId, fileId, filename, sizeBytes, contentType, fileType) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'confirmUpload',
          project_id: projectId,
          file_id: fileId,
          filename,
          size_bytes: sizeBytes,
          content_type: contentType,
          file_type: fileType
        })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to confirm upload:', error);
      throw error;
    }
  }

  /**
   * List all files for a project
   * @param {string} projectId - Project ID
   */
  async listProjectFiles(projectId) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'listProjectFiles',
          project_id: projectId
        })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();
      return result.files || [];
    } catch (error) {
      console.error('Failed to list project files:', error);
      throw error;
    }
  }

  /**
   * Delete a file from project (S3 + DynamoDB)
   * @param {string} projectId - Project ID
   * @param {string} fileId - File ID to delete
   */
  async deleteProjectFile(projectId, fileId) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'deleteProjectFile',
          project_id: projectId,
          file_id: fileId
        })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to delete project file:', error);
      throw error;
    }
  }

  /**
   * Get presigned download URL for a project file
   * @param {string} projectId - Project ID
   * @param {string} fileId - File ID to download
   */
  async getFileDownloadUrl(projectId, fileId) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'getFileDownloadUrl',
          project_id: projectId,
          file_id: fileId
        })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get file download URL:', error);
      throw error;
    }
  }

  // ============================================================================
  // MESSAGE METHODS (with session support)
  // ============================================================================

  /**
   * Save messages with safety nets
   * @param {Array} messages - Messages to save
   * @param {string} sessionId - Optional session ID (uses legacy format if not provided)
   */
  async saveMessages(messages, sessionId = null) {
    // Bypass memory API for local development
    if (BYPASS_MEMORY_API) {
      console.log('🔧 Local dev: Bypassing memory API, using localStorage');
      try {
        localStorage.setItem('chattyMessages', JSON.stringify(messages));
        return { source: 'localStorage', success: true };
      } catch (error) {
        throw new Error('Failed to save to localStorage');
      }
    }

    // Pure DynamoDB mode - no localStorage
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available - secure storage required');
    }

    try {
      const payload = {
        action: 'saveMessages',
        messages: messages
      };
      if (sessionId) {
        payload.session_id = sessionId;
      }

      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();

      return { source: 'api', success: true, ...result };

    } catch (error) {
      if (FEATURE_FLAGS.LOG_ERRORS) {
        console.error('Memory API save failed:', error);
      }
      throw error;
    }
  }

  /**
   * Atomic-append delta messages to a session. Conflict-free under concurrent
   * writes — voice transcript flush + text save can run in parallel without
   * either overwriting the other. Use this for incremental adds (post-stream
   * user/assistant turn, voice transcript flush). Use saveMessages only when
   * the caller intends to overwrite the entire list.
   * @param {Array} newMessages - ONLY the new messages to append, not the full history
   * @param {string} sessionId - Required
   */
  async appendMessages(newMessages, sessionId) {
    if (!sessionId) {
      throw new Error('appendMessages requires sessionId');
    }
    if (!Array.isArray(newMessages) || newMessages.length === 0) {
      return { source: 'noop', success: true, appended: 0 };
    }
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available - secure storage required');
    }
    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'appendMessages',
          messages: newMessages,
          session_id: sessionId,
        })
      });
      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }
      const result = await response.json();
      return { source: 'api', success: true, ...result };
    } catch (error) {
      if (FEATURE_FLAGS.LOG_ERRORS) {
        console.error('Memory API append failed:', error);
      }
      throw error;
    }
  }

  /**
   * Get messages with safety nets
   * @param {string} sessionId - Optional session ID (uses legacy format if not provided)
   */
  async getMessages(sessionId = null) {
    // Bypass memory API for local development
    if (BYPASS_MEMORY_API) {
      console.log('🔧 Local dev: Bypassing memory API, using localStorage');
      try {
        const messages = localStorage.getItem('chattyMessages');
        return messages ? JSON.parse(messages) : [];
      } catch (error) {
        console.error('Failed to parse localStorage messages:', error);
        return [];
      }
    }

    // Pure DynamoDB mode - no localStorage fallback
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available - secure storage required');
    }

    // Stream-mode path: route history load through ai_stream /history endpoint.
    // Triggers self-heal URL migration (DDB canonicalization + legacy file rename
    // to `deleting-*` on the old public bucket) + returns fresh presigned URLs.
    // Fall through to legacy path on any error so history always loads.
    // Bridge ai_stream's flat-array `media` shape to the {images, videos}
    // object MediaGallery (Message.js) expects. Live streams apply this in
    // streamingHandler.js _bucketMedia; on /history (or legacy getMessages)
    // load, no equivalent bridge ran, so MediaGallery received an array
    // (`media.images === undefined`) and rendered nothing — historical AI
    // images appeared as a small placeholder icon and click opened a new
    // tab via the browser default. Apply the bridge once at the ingestion
    // point so both paths render identically to the live stream.
    const bridgeMessageMedia = (msgs) => msgs.map((m) => {
      if (!m || typeof m !== 'object') return m;
      if (Array.isArray(m.media)) {
        const images = [];
        const videos = [];
        for (const it of m.media) {
          if (it && it.type === 'video') videos.push(it);
          else if (it) images.push(it);
        }
        return { ...m, media: { images, videos } };
      }
      return m;
    });

    if (sessionId) {
      try {
        const { isStreamModeEnabled } = await import('../components/StreamModeToggle');
        if (typeof isStreamModeEnabled === 'function' && isStreamModeEnabled()) {
          const url = `https://stream.limkc.com/history?session_id=${encodeURIComponent(sessionId)}`;
          const r = await authedFetch(url, {
            method: 'GET',
          });
          if (r.ok) {
            const data = await r.json();
            if (Array.isArray(data?.messages)) return bridgeMessageMedia(data.messages);
          }
        }
      } catch (_streamErr) {
        // fall through to legacy path
      }
    }

    try {
      const payload = { action: 'getMessages' };
      if (sessionId) {
        payload.session_id = sessionId;
      }

      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();
      const messages = result.messages || [];

      return bridgeMessageMedia(messages);

    } catch (error) {
      console.error('Memory API failed:', error);
      throw error;
    }
  }

  // Personality methods removed - obsolete with v2 backend architecture
  // The v2 backend handles all personality logic server-side

  // getPersonality method removed - obsolete with v2 backend

  /**
   * Clear messages - hybrid approach for best UX and compliance
   * @param {string} sessionId - Optional session ID to clear specific session
   */
  async clearMessages(sessionId = null) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      // Fallback: clear only localStorage if API unavailable
      try {
        localStorage.removeItem('chattyMessages');
        // chattyPersonality removed - obsolete with v2 backend
        return { source: 'localStorage', success: true, message: 'Local storage cleared' };
      } catch (error) {
        throw new Error('Failed to clear local storage');
      }
    }

    try {
      const payload = { action: 'clearMessages' };
      if (sessionId) {
        payload.session_id = sessionId;
      }

      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();
      
      // Also clear localStorage for instant UX (if not already cleared)
      try {
        localStorage.removeItem('chattyMessages');
        // chattyPersonality removed - obsolete with v2 backend
      } catch (localError) {
        console.warn('Failed to clear localStorage:', localError);
      }
      
      if (FEATURE_FLAGS.LOG_ERRORS) {
        // console.log('Messages cleared from both cloud and local storage');
      }

      return { source: 'hybrid', success: true, ...result };

    } catch (error) {
      if (FEATURE_FLAGS.LOG_ERRORS) {
        console.error('Memory API clear failed:', error);
      }
      throw error;
    }
  }

  // Helper methods for localStorage
  getMessagesFromLocalStorage() {
    try {
      const messages = localStorage.getItem('chattyMessages');
      return messages ? JSON.parse(messages) : [];
    } catch (error) {
      console.error('Failed to parse localStorage messages:', error);
      return [];
    }
  }

  // getPersonalityFromLocalStorage removed - obsolete with v2 backend

  /**
   * Migration utilities
   */
  async migrateExistingData() {
    if (!this.apiAvailable) {
      console.log('Cannot migrate - API not available');
      return;
    }

    try {
      // Migrate messages
      const localMessages = this.getMessagesFromLocalStorage();
      if (localMessages.length > 0) {
        await this.saveMessages(localMessages);
        console.log(`Migrated ${localMessages.length} messages to API`);
      }

      // Migrate personality
      // Personality migration removed - obsolete with v2 backend

      return { success: true };
    } catch (error) {
      console.error('Migration failed:', error);
      return { success: false, error: error.message };
    }
  }

  // ============================================================================
  // RAG PDF METHODS (Site-wide Reference Database)
  // ============================================================================

  /**
   * Get presigned URL for uploading a RAG PDF
   * @param {string} filename - Original filename
   * @param {number} fileSize - File size in bytes
   */
  async getRagUploadUrl(filename, fileSize) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'getRagUploadUrl',
          filename,
          content_type: 'application/pdf',
          file_size: fileSize
        })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get RAG upload URL:', error);
      throw error;
    }
  }

  /**
   * Confirm RAG PDF upload and save metadata
   * @param {string} fileId - File ID from getRagUploadUrl
   * @param {string} filename - Original filename
   * @param {number} sizeBytes - File size in bytes
   * @param {object} metadata - { title, description, category }
   */
  async confirmRagUpload(fileId, filename, sizeBytes, metadata) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'confirmRagUpload',
          file_id: fileId,
          filename,
          size_bytes: sizeBytes,
          metadata
        })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to confirm RAG upload:', error);
      throw error;
    }
  }

  /**
   * List all RAG PDFs for current user
   */
  async listRagPdfs() {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'listRagPdfs'
        })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      return data.pdfs || [];
    } catch (error) {
      console.error('Failed to list RAG PDFs:', error);
      throw error;
    }
  }

  /**
   * Delete a RAG PDF
   * @param {string} fileId - File ID to delete
   */
  async deleteRagPdf(fileId) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'deleteRagPdf',
          file_id: fileId
        })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to delete RAG PDF:', error);
      throw error;
    }
  }

  /**
   * Get presigned URL to view a RAG PDF
   * @param {string} fileId - File ID
   */
  async getRagPdfUrl(fileId) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'getRagPdfUrl',
          file_id: fileId
        })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get RAG PDF URL:', error);
      throw error;
    }
  }

  /**
   * Upload a RAG PDF (convenience method - combines getUrl + upload + confirm)
   * @param {File} file - PDF file to upload
   * @param {object} metadata - { title, description, category }
   */
  async uploadRagPdf(file, metadata) {
    // 1. Get presigned URL
    const urlResult = await this.getRagUploadUrl(file.name, file.size);
    const { upload_url, file_id } = urlResult;

    // 2. Upload to S3
    const uploadResponse = await fetch(upload_url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/pdf'
      },
      body: file
    });

    if (!uploadResponse.ok) {
      throw new Error(`S3 upload failed: ${uploadResponse.status}`);
    }

    // 3. Confirm upload with metadata
    await this.confirmRagUpload(file_id, file.name, file.size, metadata);

    return { file_id, filename: file.name };
  }

  /**
   * Update metadata for an existing RAG PDF
   * @param {string} fileId - File ID
   * @param {object} metadata - { title, description, category }
   */
  async updateRagPdfMetadata(fileId, metadata) {
    if (!FEATURE_FLAGS.USE_MEMORY_API || !this.apiAvailable) {
      throw new Error('Memory API not available');
    }

    try {
      const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'updateRagPdfMetadata',
          file_id: fileId,
          metadata
        })
      });

      if (!response.ok) {
        handleAuthError(response);
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to update RAG PDF metadata:', error);
      throw error;
    }
  }

  // ============================================================================
  // ADMIN ANALYTICS (admin/owner only — backend enforces the gate)
  // ============================================================================

  /**
   * Registered-user analytics: per-user profile (Cognito) joined with usage
   * (credits, sessions, messages, platform). Admin/owner only — the backend
   * returns 403 otherwise. Returns { users: [...], aggregates: {...} }.
   */
  async getUserAnalytics() {
    const response = await authedFetch(`${API_BASE_URL}${MEMORY_ENDPOINT}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getUserAnalytics' })
    });
    if (!response.ok) {
      handleAuthError(response);
      throw new Error(`API error: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * Get migration status
   */
  getStatus() {
    return {
      apiEnabled: FEATURE_FLAGS.USE_MEMORY_API,
      apiAvailable: this.apiAvailable,
      dualWrite: FEATURE_FLAGS.DUAL_WRITE,
      fallbackEnabled: FEATURE_FLAGS.FALLBACK_TO_LOCAL,
      localStorageMessages: this.getMessagesFromLocalStorage().length,
      localStorageHasPersonality: false // Obsolete with v2 backend
    };
  }
}

// Export singleton instance
const memoryApi = new MemoryApi();
export default memoryApi;